import { useEffect, useMemo, useState, useRef } from "react";

// Reusable components
import FormPricingInput from "@/components/formInputFields/FormPricingInput";
import FormCard from "@/components/formInputFields/FormCard";
import FormSectionTitle from "@/components/formInputFields/FormSectionTitle";

const PricingSection = ({ register, errors, setValue, watch }) => {
    // Local UI state for formatted text values
    const [priceText, setPriceText] = useState("");
    const [discountedText, setDiscountedText] = useState("");
    const [costText, setCostText] = useState("");

    const MAX_PRICE = 10_000_000;

    // Helpers mimic modal behavior
    const sanitizeMoneyOnChange = (raw) => {
      if (raw == null) return "";
      // Allow digits and a single dot; keep user intent (no leading-zero normalization here)
      let cleaned = String(raw).replace(/[^0-9.]/g, "");
      const firstDot = cleaned.indexOf(".");
      if (firstDot !== -1) {
        const intPart = cleaned.slice(0, firstDot);
        const fracRaw = cleaned.slice(firstDot + 1).replace(/\./g, "");
        const fracPart = fracRaw.slice(0, 2);
        cleaned = `${intPart}.${fracPart}`;
      }
      return cleaned;
    };

    const finalizeMoneyOnBlur = (raw) => {
      if (raw == null || raw === "") return "";
      let cleaned = String(raw).replace(/[^0-9.]/g, "");
      const firstDot = cleaned.indexOf(".");
      if (firstDot !== -1) {
        cleaned = cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, "");
      }
      let num = parseFloat(cleaned);
      if (isNaN(num)) return "";
      if (num > MAX_PRICE) num = MAX_PRICE;
      const fixed = num.toFixed(2);
      const [intPart, decPart] = fixed.split(".");
      const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      return `${withCommas}.${decPart}`;
    };

    const toNumber = (text) => {
      if (text == null || text === "") return undefined;
      const n = Number(String(text).replace(/,/g, ""));
      if (!isFinite(n)) return undefined;
      return Math.min(n, MAX_PRICE);
    };

    // Sync from form on mount and when external values change
    const watchedPrice = watch?.("productPrice");
    const watchedDiscounted = watch?.("productDiscountedPrice");
    const watchedCost = watch?.("productCostPerItem");

    // Prevent caret jump: only sync UI from form when not actively editing
    const isEditing = useRef({ price: false, discounted: false, cost: false });

    useEffect(() => {
      if (isEditing.current.price) return;
      if (watchedPrice == null || watchedPrice === "") { setPriceText(""); return; }
      const n = Number(watchedPrice);
      setPriceText(isFinite(n) ? String(n) : ""); // no commas, no forced decimals during idle sync
    }, [watchedPrice]);
    useEffect(() => {
      if (isEditing.current.discounted) return;
      if (watchedDiscounted == null || watchedDiscounted === "") { setDiscountedText(""); return; }
      const n = Number(watchedDiscounted);
      setDiscountedText(isFinite(n) ? String(n) : "");
    }, [watchedDiscounted]);
    useEffect(() => {
      if (isEditing.current.cost) return;
      if (watchedCost == null || watchedCost === "") { setCostText(""); return; }
      const n = Number(watchedCost);
      setCostText(isFinite(n) ? String(n) : "");
    }, [watchedCost]);

    // Profit & Margin (read-only)
    const numbers = useMemo(() => {
      return {
        price: toNumber(priceText),
        discounted: toNumber(discountedText),
        cost: toNumber(costText) ?? 0,
      };
    }, [priceText, discountedText, costText]);

    const effectivePrice = numbers.discounted ?? numbers.price;
    const profit =
      effectivePrice == null || numbers.cost == null || !isFinite(effectivePrice) || !isFinite(numbers.cost)
        ? ""
        : (() => {
            const diff = Math.max(0, effectivePrice - numbers.cost);
            const fixed = diff.toFixed(2);
            const [i, d] = fixed.split(".");
            return `${i.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}.${d}`;
          })();
    const margin =
      effectivePrice && effectivePrice > 0 && numbers.cost != null && isFinite(numbers.cost)
        ? `${(((effectivePrice - numbers.cost) / effectivePrice) * 100).toFixed(1)}%`
        : "";

    // Handlers for each field
    const handleChange = (setter, formKey) => (e) => {
      const next = sanitizeMoneyOnChange(e.target.value);
      setter(next);
      if (formKey === "productPrice") isEditing.current.price = true;
      if (formKey === "productDiscountedPrice") isEditing.current.discounted = true;
      if (formKey === "productCostPerItem") isEditing.current.cost = true;
      setValue?.(formKey, toNumber(next), { shouldDirty: true, shouldValidate: false });
    };
    const handleBlur = (text, setter, formKey) => () => {
      const formatted = finalizeMoneyOnBlur(text);
      setter(formatted);
      setValue?.(formKey, toNumber(formatted), { shouldDirty: true, shouldValidate: true });
      if (formKey === "productPrice") isEditing.current.price = false;
      if (formKey === "productDiscountedPrice") isEditing.current.discounted = false;
      if (formKey === "productCostPerItem") isEditing.current.cost = false;
    };

    return (
        <FormCard className="w-full max-w-3xl">
          <FormSectionTitle>Pricing</FormSectionTitle>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Product Price */}
            <FormPricingInput
              name="productPrice"
              label="Price"
              value={priceText}
              onChange={handleChange(setPriceText, "productPrice")}
              onBlur={handleBlur(priceText, setPriceText, "productPrice")}
              error={errors?.productPrice}
            />

            {/* Product Discounted Price */}
            <FormPricingInput
              name="productDiscountedPrice"
              label="Discounted price"
              value={discountedText}
              onChange={handleChange(setDiscountedText, "productDiscountedPrice")}
              onBlur={handleBlur(discountedText, setDiscountedText, "productDiscountedPrice")}
              error={errors?.productDiscountedPrice}
            />
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <FormPricingInput
              name="productCostPerItem"
              label="Cost per item"
              value={costText}
              onChange={handleChange(setCostText, "productCostPerItem")}
              onBlur={handleBlur(costText, setCostText, "productCostPerItem")}
              error={errors?.productCostPerItem}
            />

            {/* Read-only Profit */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#636363] mb-1">Profit</label>
              <div className="flex items-center rounded-md border border-[#CACACA] bg-[#F6F6F6] px-3 py-2 text-sm w-full opacity-80">
                <span className="text-[#000] mr-2 font-medium select-none">KES</span>
                <input className="bg-[#F6F6F6] w-full focus:outline-none text-[#333]" value={profit} readOnly />
              </div>
            </div>

            {/* Read-only Margin */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#636363] mb-1">Margin</label>
              <input className="w-full rounded-md border border-[#CACACA] px-4 py-2 bg-[#F6F6F6] text-[#333] text-sm" value={margin} placeholder="0%" readOnly />
            </div>
          </div>
        </FormCard>
    );
};

export default PricingSection;
